/**
 * Der Steckbrief eines Linebound als Overlay (15.10.2026).
 *
 * Seit die Auswahl Portraets zeigt, steht auf der Kachel nur noch Name und
 * Rolle. Hier kann man vor der Wahl nachlesen, was die Figur kann: Passive,
 * die vier Faehigkeiten, ihre Ausbauwege und die Zahlen.
 *
 * WAS darin steht, entscheidet steckbriefModell.ts - und das ist ohne Bild
 * geprueft. Diese Datei stellt es nur hin.
 *
 * DAS AUSSEHEN (Grafiksitzung, 15.10.2026): links das grosse Portraet,
 * rechts der Text im Stil des Menues - goldene Ecken, Serifenschrift. Der
 * Text scrollt fuer sich; "Pick" und "Close" stehen fest darunter und sind
 * immer zu sehen, auch am Handy im Querformat.
 *
 * Er haengt per Portal an document.body - wie die Aufgeben-Rueckfrage. Sonst
 * laege er innerhalb des Menuekastens und damit unter allem, was darueber
 * kommt; eine hohe z-index-Zahl hilft dagegen nicht.
 *
 * COUNTERPICKING (Produktentscheidung vom 05.10.2026): Der Steckbrief zeigt
 * nur, was man selbst oeffnet. Die Wahl des Gegners verraet er nie - er
 * bekommt gar nicht erst zu sehen, wer gegenueber sitzt.
 */

import { createPortal } from 'react-dom';
import { t } from '../i18n/index.js';
import { LINEBOUND_BILDER } from './menueBilder.js';
import { getSkillInSlot } from '@dueline/game-data';
import { faehigkeitBild, passivBild } from './faehigkeitBilder.js';
import type { Bildquellen } from './menueBilder.js';

/** Ein kleines rundes Symbol vor einer Zeile - oder nichts, wenn es keines gibt. */
function Symbol({ bild }: { bild: Bildquellen | undefined }) {
  if (!bild) return null;
  return (
    <picture className="dl-steckbrief__symbol" aria-hidden="true">
      <source type="image/avif" srcSet={bild.avif} sizes="48px" />
      <source type="image/webp" srcSet={bild.webp} sizes="48px" />
      <img src={bild.ersatz} alt="" decoding="async" />
    </picture>
  );
}
import { baueSteckbrief, type SteckbriefFaehigkeit } from './steckbriefModell.js';

interface Props {
  /** Welcher Linebound gezeigt wird. */
  heroId: string;
  /** Diesen Linebound waehlen und schliessen. Fehlt er, gibt es kein "Pick". */
  onWaehle?: (heroId: string) => void;
  onSchliessen: () => void;
}

/** Die Ueberschrift einer Faehigkeit: "Skill 1" oder "Ultimate". */
function slotName(slot: SteckbriefFaehigkeit['slot']): string {
  return slot === 4 ? t.steckbrief.ultimate : t.steckbrief.slot(slot);
}

export function LineboundSteckbrief({ heroId, onWaehle, onSchliessen }: Props) {
  const steckbrief = baueSteckbrief(heroId);
  if (!steckbrief) return null;
  const bild = LINEBOUND_BILDER[steckbrief.linebound.toLowerCase()];

  return createPortal(
    <div className="dl-steckbrief" role="dialog" aria-label={steckbrief.linebound}>
      <div className="dl-steckbrief__kasten">
        {bild && (
          <picture className="dl-steckbrief__bild">
            <source type="image/avif" srcSet={bild.avif} sizes="40vw" />
            <source type="image/webp" srcSet={bild.webp} sizes="40vw" />
            <img src={bild.ersatz} alt="" decoding="async" />
          </picture>
        )}

        <div className="dl-steckbrief__seite">
          <div className="dl-steckbrief__text">
            <h2 className="dl-steckbrief__titel">{steckbrief.linebound}</h2>
            <p className="dl-steckbrief__rolle">{steckbrief.rolle}</p>
            <p className="dl-steckbrief__satz">{steckbrief.satz}</p>
            <p className="dl-steckbrief__satz">{steckbrief.rollenSatz}</p>

            {steckbrief.passive && (
              <section className="dl-steckbrief__teil">
                <h3 className="dl-steckbrief__teilTitel">{t.passive.label}</h3>
                <div className="dl-steckbrief__mitSymbol">
                  <Symbol bild={passivBild(heroId)} />
                  <p className="dl-steckbrief__zeile">
                    <strong>{steckbrief.passive.name}</strong> {steckbrief.passive.beschreibung}
                  </p>
                </div>
              </section>
            )}

            <section className="dl-steckbrief__teil">
              <h3 className="dl-steckbrief__teilTitel">{t.steckbrief.abilities}</h3>
              {steckbrief.faehigkeiten.map((faehigkeit) => (
                <div key={faehigkeit.slot} className="dl-steckbrief__faehigkeit">
                  <div className="dl-steckbrief__mitSymbol">
                    <Symbol
                      bild={(() => {
                        const skill = getSkillInSlot(heroId, faehigkeit.slot);
                        return skill ? faehigkeitBild(skill.id) : undefined;
                      })()}
                    />
                    <p className="dl-steckbrief__zeile">
                      <span className="dl-steckbrief__slot">{slotName(faehigkeit.slot)}</span>{' '}
                      <strong>{faehigkeit.name}</strong> {faehigkeit.beschreibung}
                    </p>
                  </div>

                  {/* Die Ausbauwege haengen am Skill - deshalb stehen sie hier
                  und nicht in einem eigenen Abschnitt weiter unten. */}
                  {faehigkeit.ausbau.length > 0 && (
                    <ul className="dl-steckbrief__ausbau">
                      {faehigkeit.ausbau.map((ausbau) => (
                        <li key={ausbau.name}>
                          <strong>{ausbau.name}</strong> {ausbau.beschreibung}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </section>

            <section className="dl-steckbrief__teil">
              <h3 className="dl-steckbrief__teilTitel">{t.steckbrief.choices}</h3>
              <ul className="dl-steckbrief__ausbau">
                {steckbrief.wahlen.map((wahl) => (
                  <li key={wahl.name}>
                    <strong>{wahl.name}</strong> {wahl.beschreibung}
                  </li>
                ))}
              </ul>
            </section>

            <section className="dl-steckbrief__teil">
              <h3 className="dl-steckbrief__teilTitel">{t.steckbrief.stats}</h3>
              <p className="dl-steckbrief__zeile">
                {steckbrief.werte.map((wert) => `${wert.label} ${wert.wert}`).join('  ·  ')}
              </p>
            </section>
          </div>

          <div className="dl-steckbrief__knoepfe">
            {/*
            "Pick" fehlt, wenn die Wahl gerade gesperrt ist - im Countdown
            etwa. Dann bleibt der Steckbrief zum Nachlesen offen, nur waehlen
            kann man nicht mehr.
          */}
            {onWaehle && (
              <button
                type="button"
                className="dl-button dl-button--primary"
                onClick={() => onWaehle(heroId)}
              >
                {t.steckbrief.pick(steckbrief.linebound)}
              </button>
            )}
            <button type="button" className="dl-button" onClick={onSchliessen}>
              {t.steckbrief.close}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
